/**
 * Category Event Service
 * 
 * Provides real-time synchronization of inventory categories across components.
 * When a category is added/deleted in Settings, all listening components are notified.
 * 
 * Usage:
 *   import categoryEventService from '../services/categoryEventService';
 *   
 *   // Subscribe to events
 *   const unsubscribe = categoryEventService.subscribe((event) => {
 *     console.log('Category event:', event);
 *   });
 *   
 *   // Emit events
 *   categoryEventService.categoryAdded('New Category');
 *   categoryEventService.categoryDeleted('Old Category');
 *   
 *   // Cleanup
 *   unsubscribe();
 */

class CategoryEventService {
  constructor() {
    this.listeners = [];
  }

  /**
   * Subscribe to category change events
   * @param {Function} callback - Called with { type: 'added'|'deleted', category: string }
   * @returns {Function} Unsubscribe function
   */
  subscribe(callback) {
    if (typeof callback !== 'function') {
      console.error('CategoryEventService: callback must be a function');
      return () => {};
    }
    
    this.listeners.push(callback);
    
    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback);
    };
  }

  /**
   * Notify all listeners about a category change
   * @param {Object} event - { type: 'added'|'deleted', category: string }
   */
  notify(event) {
    if (!event || !event.type || !event.category) {
      console.error('CategoryEventService: Invalid event format', event);
      return;
    }
    
    
    this.listeners.forEach((callback, index) => {
      try {
        callback(event);
      } catch (error) {
        console.error(`CategoryEventService: Error in listener ${index}:`, error);
      }
    });
  }

  /**
   * Emit category added event
   * @param {string} categoryName 
   */
  categoryAdded(categoryName) {
    if (!categoryName || typeof categoryName !== 'string') {
      console.error('CategoryEventService: Invalid category name for added event');
      return;
    }
    this.notify({ type: 'added', category: categoryName });
  }

  /**
   * Emit category deleted event
   * @param {string} categoryName 
   */
  categoryDeleted(categoryName) {
    if (!categoryName || typeof categoryName !== 'string') {
      console.error('CategoryEventService: Invalid category name for deleted event');
      return;
    }
    this.notify({ type: 'deleted', category: categoryName });
  }

  /**
   * Get current number of active listeners (for debugging)
   */
  getListenerCount() {
    return this.listeners.length;
  }
}

// Export singleton instance
const categoryEventService = new CategoryEventService();

export default categoryEventService;
